import { flatMap, groupBy } from 'lodash'

/**
 * Filters out DST (Daylight Saving Time) transition duplicates that have all null values.
 *
 * During DST transitions, the same local time can appear twice in UTC-based data grids.
 * This function removes duplicate local times that have no data, while preserving:
 * - Legitimate data gaps (null values with unique local times)
 * - All data points that have actual values
 *
 * @param data - Array of objects with a timestamp property
 * @param options - Configuration options
 * @param options.timestampKey - Key name for the timestamp property (default: 'timestamp')
 * @param options.valueKey - Key to check for actual data. If not provided, auto-detects from first element:
 *   Priority: value_kwh > value_kw > value_m3 > value_m3h > value
 *   Set to null to check all keys except timestamp
 * @returns Filtered array with DST duplicates removed
 *
 * @example
 * // Basic usage - auto-detects value key from first element
 * const filtered = filterDstDuplicates(data)
 *
 * @example
 * // Custom timestamp key
 * const filtered = filterDstDuplicates(data, { timestampKey: 'time' })
 *
 * @example
 * // Explicitly specify which key to check for data
 * const filtered = filterDstDuplicates(data, {
 *   valueKey: 'consumption'
 * })
 *
 * @example
 * // Check all non-timestamp keys
 * const filtered = filterDstDuplicates(data, { valueKey: null })
 */
export function filterDstDuplicates<T extends Record<string, any>>(
  data: T[],
  options: {
    timestampKey?: string
    valueKey?: string | null
  } = {}
): T[] {
  if (!data || data.length === 0) return []
  const { timestampKey = 'timestamp', valueKey } = options
  const timestampRegex = /^(\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(:\d{2})?)/

  // Auto-detect valueKey if not provided
  let detectedValueKey: string | null = valueKey ?? null
  if (valueKey === undefined && data.length > 0) {
    const firstElement = data[0]
    const valuePriority = ['value_kwh', 'value_kw', 'value_m3', 'value_m3h', 'value']
    detectedValueKey = valuePriority.find(key => key in firstElement) ?? null
  }

  // Helper to check if a data point has actual data (non-null values)
  const hasData = (dataPoint: T) => {
    // If valueKey is explicitly null, check all keys except timestamp
    if (detectedValueKey === null) {
      return Object.keys(dataPoint).some(key => key !== timestampKey && dataPoint[key] !== null)
    }
    // Otherwise check the specific valueKey
    return dataPoint[detectedValueKey] !== null
  }

  // Group all entries by their local timestamp
  const groupedByTime = groupBy(data, dataPoint => {
    const timestamp = dataPoint[timestampKey] as string
    const match = timestamp.match(timestampRegex)
    return match?.[1] ?? timestamp
  })

  // Process each group and flatten the results
  return flatMap(Object.values(groupedByTime), group => {
    // If it's not a duplicate local time, keep it
    if (group.length === 1) return group

    // It's a duplicate, so filter for entries that have actual data
    const entriesWithData = group.filter(hasData)

    // If any duplicates have data, keep only those with data
    // Otherwise, keep only the first one to represent the gap
    return entriesWithData.length > 0 ? entriesWithData : [group[0]]
  })
}
